import React, { Component, useState } from 'react';
import { AddToClipBoard } from './../../utils/functions';
import {
  Colors,
  BrandColors,
  SpendhoundColors,
  ProductColors,
  CategoricalColors,
  generateSequentialPalette,
  generateDivergingPalette,
} from './../../utils/designConstants';
import ColorSquare from './colorSquare';
import DesignSection from './designSection';
import SectionBanner from './sectionBanner';
import SectionPageHeader from './sectionPageHeader';

export default class ColorSection extends Component {
  constructor() {
    super();

    this.defaultSequentialSteps = 6;
    this.defaultDivergingSteps = 6;

    this.state = {
      sequentialColor: CategoricalColors[0],
      sequentialSteps: this.defaultSequentialSteps,
      divergingColorOne: CategoricalColors[0],
      divergingColorTwo: CategoricalColors[1],
      divergingSteps: this.defaultDivergingSteps,
    };
  }

  updateSequentialColor = (e) => {
    if (e.target.value) {
      this.setState({ sequentialColor: CategoricalColors[e.target.value] });
    }
  };

  updateSequentialSteps = (e) => {
    if (e.target.value) {
      this.setState({ sequentialSteps: parseInt(e.target.value) });
    }
  };

  updateDivergingColor = (e, order) => {
    if (e.target.value) {
      if (order === 1) {
        this.setState({ divergingColorOne: CategoricalColors[e.target.value] });
      } else {
        this.setState({ divergingColorTwo: CategoricalColors[e.target.value] });
      }
    }
  };

  updateDivergingSteps = (e) => {
    if (e.target.value) {
      this.setState({ divergingSteps: parseInt(e.target.value) });
    }
  };

  render() {
    const {
      sequentialColor,
      sequentialSteps,
      divergingColorOne,
      divergingColorTwo,
      divergingSteps,
    } = this.state;

    const sequentialPickerNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    const divergingPickerNumbers = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19];

    const sequentialPalette = generateSequentialPalette(sequentialColor, sequentialSteps);
    const divergingPalette = generateDivergingPalette(
      divergingColorOne,
      divergingColorTwo,
      divergingSteps
    );
    const half = Math.floor(divergingPalette.length / 2);
    const isOddPalette = divergingPalette.length % 2 === 1;
    const orderedDivergingPalette = [
      ...divergingPalette.slice(0, half).reverse(),
      ...(isOddPalette ? [divergingPalette[half]] : []),
      ...divergingPalette.slice(half + (isOddPalette ? 1 : 0)).reverse(),
    ];

    return (
      <div className="full-width sectionContainer">
        <SectionBanner image="/images/background-lines-gradient-purple.webp" title="Colors" />
        <SectionPageHeader description="Our color palette is a core part of how we express our brand and create visual consistency across every touchpoint. Using approved colors — including our ui, chart and data visualization palettes — ensures our work feels cohesive, accessible, and immediately recognizable as YipitData." />
        <div id="brand-colors">
          <div style={{ borderTop: '1px solid rgba(0,0,0,0.08)', margin: '48px 0 24px' }} />
          <div className="max-width-container">
            <h2 className="small-padding">{'Branding'}</h2>
          </div>
          <DesignSection
            title={'YipitData Brand Colors'}
            subtitle={'These colors are reserved for marketing and brand expression, reflecting the Yipit logo palette, and should not be used as part of the product UI design system.'}>
            <div className="stretch-row full-width" style={{ overflowX: 'auto' }}>
              {BrandColors.map((color, index) => (
                <ColorSquare color={color} key={color + index} />
              ))}
            </div>
          </DesignSection>
          <DesignSection
            title={'Spendhound Brand Colors'}
            subtitle={'These colors define the SpendHound brand, providing a cohesive visual identity across marketing, product experiences, and customer touchpoints.'}>
            <div className="stretch-row full-width" style={{ overflowX: 'auto' }}>
              {SpendhoundColors.map((color, index) => (
                <ColorSquare color={color} key={color + index} />
              ))}
            </div>
          </DesignSection>
        </div>
        <div id="chart-colors">
          <div style={{ borderTop: '1px solid rgba(0,0,0,0.08)', margin: '48px 0 24px' }} />
          <div className="max-width-container">
            <h2 className="small-padding">{'Charts & Visualizations'}</h2>
          </div>
        <div id="chart-core-colors">
        <DesignSection
          title={'Core Colors'}
          subtitle={"When used in the order they're listed, these colors have enough differentiation to create clean and accessible friendly visualizations."}>
          <div className="small-max-width-container" style={{ margin: '0', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {CategoricalColors.map((color, index) => (
              <div key={color.hex + index} style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
                <ColorPaletteRow hex={color.hex} label={color.name} />
              </div>
            ))}
          </div>
        </DesignSection>
        </div>
        <div id="chart-sequential-colors">
        <DesignSection
          title={'Sequential Colors'}
          subtitle={'Sequential color palette should be used to establish a visual heirarchy. We want these colors to tell a story using the color stops as a way to better illustrate importance. Choose a color below to create a base and the amount of steps you need and we will generate the appropriate palette for the visualization.'}>
          <div className="row">
            <Picker
              subheading={'Color'}
              onChange={this.updateSequentialColor}
              style={{ marginRight: '20px' }}>
              {CategoricalColors.map((color, index) => {
                return (
                  <option value={index} key={color.name}>
                    {color.name}
                  </option>
                );
              })}
            </Picker>
            <Picker
              defaultValue={this.defaultSequentialSteps}
              subheading={'Steps'}
              onChange={this.updateSequentialSteps}
              style={{ marginRight: '20px' }}>
              {sequentialPickerNumbers.map((val, index) => {
                return (
                  <option value={val} key={val}>
                    {val}
                  </option>
                );
              })}
            </Picker>
          </div>
          <div className="small-max-width-container" style={{ margin: '20px 0 0 0', borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
            {sequentialPalette.map((hex, index) => (
              <ColorPaletteRow
                key={hex + index}
                hex={hex}
                label={`${sequentialColor.name} ${stepToMD[sequentialColor.steps.findIndex(s => s.toLowerCase() === hex.toLowerCase())]}`}
              />
            ))}
          </div>
        </DesignSection>
        </div>
        <div id="chart-diverging-colors">
        <DesignSection
          title={'Diverging Colors'}
          subtitle={'Diverging color palettes are cool.'}>
          <div className="row full-width">
            <Picker
              subheading={'Color 1'}
              onChange={(e) => this.updateDivergingColor(e, 1)}
              defaultValue={0}
              style={{ marginRight: '20px' }}>
              {CategoricalColors.map((color, index) => {
                return (
                  <option value={index} key={color.name}>
                    {color.name}
                  </option>
                );
              })}
            </Picker>
            <Picker
              subheading={'Color 2'}
              onChange={(e) => this.updateDivergingColor(e, 2)}
              defaultValue={1}
              style={{ marginRight: '20px' }}>
              {CategoricalColors.map((color, index) => {
                return (
                  <option value={index} key={color.name}>
                    {color.name}
                  </option>
                );
              })}
            </Picker>
            <Picker
              subheading={'Steps'}
              onChange={this.updateDivergingSteps}
              style={{ marginRight: '20px' }}
              defaultValue={this.defaultDivergingSteps}>
              {divergingPickerNumbers.map((val, index) => {
                return (
                  <option value={val} key={val}>
                    {val}
                  </option>
                );
              })}
            </Picker>
          </div>
          <div className="small-max-width-container" style={{ margin: '20px 0 0 0', borderRadius: '8px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
            {orderedDivergingPalette.map((hex, index) => (
              <ColorPaletteRow
                key={hex + index}
                hex={hex}
                label={getDivergingLabel(hex, divergingColorOne, divergingColorTwo)}
              />
            ))}
          </div>
        </DesignSection>
        </div>
        </div>
        <div id="ui-colors">
          <div style={{ borderTop: '1px solid rgba(0,0,0,0.08)', margin: '48px 0 24px' }} />
          <div className="max-width-container">
            <h2 className="small-padding">{'User Interfaces'}</h2>
          </div>
        <DesignSection
          title={'Design System Colors'}
          subtitle={'A systematic approach to color in our interfaces — from backgrounds and surfaces to text, borders, and interactive states. A consistent UI color system ensures that every product experience feels intentional, accessible, and unmistakably YipitData.'}>
          <div className="row medium-bottom-margin" style={{ alignItems: 'flex-start' }}>
            <div className="small-padding primary-background-subtle primary-color-dark" style={{ borderRadius: '12px', display: 'flex', alignItems: 'flex-start', gap: '12px', maxWidth: '480px' }}>
              <span className="material-symbols-rounded primary-color" style={{ fontSize: '24px', flexShrink: 0 }}>construction</span>
              <div>
                <p style={{ margin: 0, fontWeight: 600, opacity: 1 }}>{'Coming Soon'}</p>
                <p style={{ margin: 0 }}>{'The UI color system is currently under construction. Check back soon for guidance on semantic color tokens, surface hierarchy, and interactive states.'}</p>
              </div>
            </div>
          </div>
        </DesignSection>
        </div>
      </div>
    );
  }
}

const stepToMD = [100, 200, 300, 400, 500, 600, 700, 800, 900, 50];

function getDivergingLabel(hex, color1, color2) {
  const normalized = hex.toLowerCase();
  if (normalized === Colors['black'].steps[8].toLowerCase()) return 'Neutral';
  const idx1 = color1.steps.findIndex(s => s.toLowerCase() === normalized);
  if (idx1 !== -1) return `${color1.name} ${stepToMD[idx1]}`;
  const idx2 = color2.steps.findIndex(s => s.toLowerCase() === normalized);
  if (idx2 !== -1) return `${color2.name} ${stepToMD[idx2]}`;
  return hex;
}

function ColorPaletteRow({ hex, label }) {
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);
  const clean = hex.replace('#', '').toLowerCase();
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const textColor = luminance > 0.5 ? 'rgba(0,0,0,0.7)' : 'rgba(255,255,255,0.9)';

  const handleCopy = () => {
    AddToClipBoard(clean);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      onClick={handleCopy}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: hex,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 20px',
        cursor: 'pointer',
        opacity: hovered ? 0.9 : 1,
        transition: 'opacity 0.15s ease',
      }}
    >
      <span style={{ color: textColor, fontWeight: 500, fontSize: '0.9rem' }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {hovered && !copied && (
          <span style={{ color: textColor, fontSize: '0.75rem', opacity: 0.7 }}>
            (click to copy)
          </span>
        )}
        <span style={{ color: textColor, fontFamily: 'monospace', fontSize: '0.85rem', opacity: 0.85 }}>
          {copied ? 'Copied!' : '#' + clean.toUpperCase()}
        </span>
      </div>
    </div>
  );
}

function Picker({ subheading, onChange, style, defaultValue, children }) {
  return (
    <div style={style}>
      <h7 className="subheading uppercase bold-text" style={{display:"block", marginBottom:'10px'}}>{subheading}</h7>
      <select
        defaultValue={defaultValue ? defaultValue.toString() : null}
        onChange={(e) => onChange(e)}
        style={{ marginRight: '20px', marginBottom:'20px' }}
      >
        {children}
      </select>
    </div>
  );
}
